import { otsBaseApi } from '../../ots-api';
import * as t from 'io-ts';
import {
  SystemProcessExecution,
  SystemProcessExecutionCodec,
  SystemProcessExecutionPageSearchCriteria,
  SystemProcessExecutionSearchCriteria,
} from '../../../model/system-process-execution';
import {
  SystemProcessExecutionArtifact,
  SystemProcessExecutionArtifactCodec,
} from '../../../model/system-process-execution-artifact';
import {
  SystemProcessExecutionIssue,
  SystemProcessExecutionIssueCodec,
} from '../../../model/system-process-execution-issue';
import {
  SystemProcessExecutionMetric,
  SystemProcessExecutionMetricCodec,
} from '../../../model/system-process-execution-metric';
import { removeUndefined } from '../../../util/util';
import {
  SystemProcessExecutionMissingMapping,
  SystemProcessExecutionMissingMappingCodec,
} from '../../../model/system-process-execution-missing-mapping';
import { Page, PagedResponseCodec } from '../../../model/paged-response';
import { PageSpec } from '../../../model/search/page-spec';
import { validateJSONNow } from '../../../web/validation';

export type GetSystemProcessExecutionMissingMappingsPageArgs = {
  id: string;
  page: PageSpec;
};

const ALL_TAG_TYPES = [
  'SystemProcessExecutions',
  'SystemProcessExecutionArtifacts',
  'SystemProcessExecutionIssues',
  'SystemProcessExecutionMetrics',
  'SystemProcessExecutionMissingMappings',
] as const;

const taggedBaseApi = otsBaseApi.enhanceEndpoints({
  addTagTypes: ALL_TAG_TYPES,
});

export const systemProcessExecutionsApi = taggedBaseApi.injectEndpoints({
  endpoints: (builder) => ({
    getAllSystemProcessExecutions: builder.query<
      SystemProcessExecution[],
      SystemProcessExecutionSearchCriteria
    >({
      query: (arg) => ({
        url: `/utils/system-process-exec/all`,
        method: 'GET',
        params: removeUndefined({
          ...arg,
        }),
      }),
      transformResponse: (response: unknown) =>
        validateJSONNow(t.array(SystemProcessExecutionCodec), response),
      providesTags: (result) => {
        if (result) {
          return [
            ...result.map(
              ({ id }) => ({ type: 'SystemProcessExecutions', id } as const)
            ),
            { type: 'SystemProcessExecutions', id: 'LIST' },
          ];
        } else {
          return [{ type: 'SystemProcessExecutions', id: 'LIST' }];
        }
      },
    }),
    getSystemProcessExecutionsPage: builder.query<
      Page<SystemProcessExecution>,
      SystemProcessExecutionPageSearchCriteria
    >({
      // Keep list pages warm while users move between execution details and the search results.
      keepUnusedDataFor: 30 * 60,
      query: (arg) => ({
        url: `/utils/system-process-exec/search/page`,
        method: 'GET',
        params: removeUndefined({ ...arg }),
      }),
      transformResponse: (response: unknown) =>
        validateJSONNow(
          PagedResponseCodec(SystemProcessExecutionCodec),
          response
        ),
      providesTags: (result) =>
        result
          ? [
              ...result.content.map(
                ({ id }) => ({ type: 'SystemProcessExecutions', id } as const)
              ),
              { type: 'SystemProcessExecutions', id: 'LIST' },
            ]
          : [{ type: 'SystemProcessExecutions', id: 'LIST' }],
    }),
    getSystemProcessExecution: builder.query<SystemProcessExecution, string>({
      query: (id) => ({
        url: `/utils/system-process-exec/${id}`,
        method: 'GET',
      }),
      transformResponse: (response: unknown) =>
        validateJSONNow(SystemProcessExecutionCodec, response),
      providesTags: (result, error, id) => [
        { type: 'SystemProcessExecutions', id },
      ],
    }),
    getSystemProcessExecutionArtifacts: builder.query<
      SystemProcessExecutionArtifact[],
      string
    >({
      query: (id) => ({
        url: `/utils/system-process-exec/${id}/artifacts`,
        method: 'GET',
      }),
      transformResponse: (response: unknown) =>
        validateJSONNow(t.array(SystemProcessExecutionArtifactCodec), response),
      providesTags: (result, error, id) => [
        { type: 'SystemProcessExecutionArtifacts', id },
      ],
    }),
    getSystemProcessExecutionIssues: builder.query<
      SystemProcessExecutionIssue[],
      string
    >({
      query: (id) => ({
        url: `/utils/system-process-exec/${id}/issues`,
        method: 'GET',
      }),
      transformResponse: (response: unknown) =>
        validateJSONNow(t.array(SystemProcessExecutionIssueCodec), response),
      providesTags: (result, error, id) => [
        { type: 'SystemProcessExecutionIssues', id },
      ],
    }),
    getSystemProcessExecutionMetrics: builder.query<
      SystemProcessExecutionMetric[],
      string
    >({
      query: (id) => ({
        url: `/utils/system-process-exec/${id}/metrics`,
        method: 'GET',
      }),
      transformResponse: (response: unknown) =>
        validateJSONNow(t.array(SystemProcessExecutionMetricCodec), response),
      providesTags: (result, error, id) => [
        { type: 'SystemProcessExecutionMetrics', id },
      ],
    }),
    getSystemProcessExecutionMissingMappings: builder.query<
      Page<SystemProcessExecutionMissingMapping>,
      GetSystemProcessExecutionMissingMappingsPageArgs
    >({
      query: ({ id, page }) => ({
        url: `/utils/system-process-exec/${id}/missing-mappings/page`,
        method: 'GET',
        params: page,
      }),
      transformResponse: (response: unknown) =>
        validateJSONNow(
          PagedResponseCodec(SystemProcessExecutionMissingMappingCodec),
          response
        ),
      providesTags: (result, error, { id }) => [
        { type: 'SystemProcessExecutionMissingMappings', id },
      ],
    }),
  }),
});

export const {
  useGetAllSystemProcessExecutionsQuery,
  useGetSystemProcessExecutionsPageQuery,
  useGetSystemProcessExecutionQuery,
  useGetSystemProcessExecutionArtifactsQuery,
  useGetSystemProcessExecutionIssuesQuery,
  useGetSystemProcessExecutionMetricsQuery,
  useGetSystemProcessExecutionMissingMappingsQuery,
} = systemProcessExecutionsApi;
